import React, { useEffect, useState } from 'react';

import globalMessages from 'translations/messages';
import { Button, Col, Form, Row, Space } from 'antd';
import useFormatMessage from 'hooks/useFormatMessage';
import Loading from 'components/Loading';
import useGlobalContext from 'hooks/useGlobalContext';
import { Prompt } from 'react-router';
import useUserConfirmation from 'hooks/useUserConfirmation';
import Modal from 'components/Modal';
import { FormattedMessage } from 'react-intl';

const ModalForm = ({
  open,
  onCancel,
  onDone,
  onSubmit,
  onRemove,
  onSuccess,
  onError,
  okText = <FormattedMessage {...globalMessages.save} />,
  removeLabel,
  name,
  title,
  children,
  formReset,
  loading,
  width,
  fullWidth,
  onValuesChange,
  footer,
  ...rest
}) => {
  const $t = useFormatMessage();
  const [form] = Form.useForm();
  const { message, modal } = useGlobalContext();
  const [saving, setSaving] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const getUserConfirmation = useUserConfirmation();

  useEffect(() => {
    setIsTouched(false);
    form.resetFields();
  }, [formReset, open]);

  const onFinish = async (values) => {
    try {
      setSaving(true);
      const res = await onSubmit(values);
      if (res?.errors) {
        throw new Error(res.errors[0].message);
      }
      if (onSuccess) {
        onSuccess(res);
      } else {
        message.success($t(globalMessages.successfullFormFeedback));
      }
      if (onDone) {
        onDone(res);
      } else {
        onCancel();
      }
    } catch (e) {
      console.error(e);
      if (onError) {
        onError(e.message);
      } else {
        message.error($t(globalMessages.errorFormFeedback));
      }
    } finally {
      setSaving(false);
    }
  };

  const removeHandler = async () => {
    modal.confirm({
      title: $t(globalMessages.confirmDelete, { title: removeLabel }),
      okText: $t(globalMessages.delete),
      okType: 'danger',
      confirmLoading: true,
      onOk: onRemove,
    });
  };

  const safeCancel = () => {
    if (isTouched) {
      getUserConfirmation($t(globalMessages.unsavedChanges), ok => { if (ok) onCancel(); });
    } else {
      onCancel();
    }
  };

  const onChange = (changedValues, allValues) => {
    setIsTouched(true);
    if (onValuesChange) {
      onValuesChange(changedValues, allValues);
    }
  };

  return (
    <Modal
      name={name}
      title={title}
      open={open}
      onCancel={safeCancel}
      onOk={form.submit}
      okText={okText}
      confirmLoading={saving}
      onRemove={onRemove && removeHandler}
      width={width}
      fullWidth={fullWidth}
      forceRender
      footer={footer !== undefined ? footer : ((_, { OkBtn, CancelBtn }) => (
        <Row justify={onRemove ? 'space-between' : 'end'}>
          {onRemove && (
            <Col>
              <Button danger onClick={removeHandler} className={name && `${name}-remove`}>
                <FormattedMessage {...globalMessages.delete} />
              </Button>
            </Col>
          )}
          <Col>
            <Space>
              <CancelBtn />
              <OkBtn />
            </Space>
          </Col>
        </Row>
      ))}
    >
      <Form layout="vertical" form={form} onFinish={onFinish} onValuesChange={onChange} {...rest}>
        {open && (
          <>
            <Prompt when={isTouched} message={$t(globalMessages.unsavedChanges)} />
            {loading ? <Loading /> : children}
          </>
        )}
      </Form>
    </Modal>
  );
};

export default ModalForm;
